import React, { useEffect, useState } from 'react';
import { supabase } from '../../config/supabase';
import './Admin.css';

const Dashboard = () => {
    const [stats, setStats] = useState({
        enquiries: 0,
        sessions: 0,
        events: 0,
        jobs: 0,
        users: 0
    });
    const [recentEnquiries, setRecentEnquiries] = useState([]);
    const [recentSessions, setRecentSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        fetchDashboardData();
        const interval = setInterval(fetchDashboardData, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const [
                enquiriesResult,
                sessionsResult,
                eventsResult,
                jobsResult,
                usersResult,
                recentEnquiriesResult,
                recentSessionsResult
            ] = await Promise.all([
                supabase.from('contact_messages').select('id', { count: 'exact', head: true }),
                supabase.from('sessions').select('id', { count: 'exact', head: true }),
                supabase.from('events').select('id', { count: 'exact', head: true }),
                supabase.from('jobs').select('id', { count: 'exact', head: true }),
                supabase.from('users').select('id', { count: 'exact', head: true }),
                supabase.from('contact_messages').select('*').order('created_at', { ascending: false }).limit(5),
                supabase.from('sessions').select('*').order('created_at', { ascending: false }).limit(5)
            ]);

            setStats({
                enquiries: enquiriesResult.count || 0,
                sessions: sessionsResult.count || 0,
                events: eventsResult.count || 0,
                jobs: jobsResult.count || 0,
                users: usersResult.count || 0
            });

            setRecentEnquiries(recentEnquiriesResult.data || []);
            setRecentSessions(recentSessionsResult.data || []);
            setLastUpdated(new Date().toLocaleTimeString());

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <h1>Dashboard</h1>
                {lastUpdated && (
                    <span className="last-updated">
                        Last updated: {lastUpdated}
                    </span>
                )}
            </header>

            {loading ? (
                <div className="loading-state">
                    Loading dashboard data...
                </div>
            ) : (
                <>
                    {/* Stats Cards */}
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}> Enquiries</div>
                            <div className="stat-info">
                                <h3>{stats.enquiries}</h3>
                                <p>Total Enquiries</p>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#e8f5e9', color: '#388e3c' }}> Sessions</div>
                            <div className="stat-info">
                                <h3>{stats.sessions}</h3>
                                <p>Session Requests</p>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#fff3e0', color: '#f57c00' }}> Events</div>
                            <div className="stat-info">
                                <h3>{stats.events}</h3>
                                <p>Events</p>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#fce4ec', color: '#c2185b' }}> Jobs</div>
                            <div className="stat-info">
                                <h3>{stats.jobs}</h3>
                                <p>Job Postings</p>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}> Users</div>
                            <div className="stat-info">
                                <h3>{stats.users}</h3>
                                <p>Registered Users</p>
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="recent-activity">
                        <div className="activity-section">
                            <h2>Recent Enquiries</h2>
                            <div className="activity-list">
                                {recentEnquiries.length > 0 ? recentEnquiries.map(enq => (
                                    <div key={enq.id} className="activity-item">
                                        <div className="activity-info">
                                            <strong>{enq.name}</strong>
                                            <span>{enq.subject}</span>
                                        </div>
                                        <span className={`status-badge ${enq.status?.toLowerCase()}`}>
                                            {enq.status || 'New'}
                                        </span>
                                    </div>
                                )) : (
                                    <p className="empty-state">No recent enquiries</p>
                                )}
                            </div>
                        </div>

                        <div className="activity-section">
                            <h2>Recent Sessions</h2>
                            <div className="activity-list">
                                {recentSessions.length > 0 ? recentSessions.map(session => (
                                    <div key={session.id} className="activity-item">
                                        <div className="activity-info">
                                            <strong>{session.full_name}</strong>
                                            <span>{session.topic}</span>
                                        </div>
                                        <span className="date">
                                            {session.session_date || 'Pending'}
                                        </span>
                                    </div>
                                )) : (
                                    <p className="empty-state">No recent sessions</p>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
